import React, { useEffect, useState } from 'react';
import { andOfElements, isMobile, orOfElements } from '../../utils/commonUtils';
import RouteCabsV2 from '../RouteCabsV2/RouteCabsV2';
import Slides from '../Slides/Slides';
import BreadCrumb from '../BreadCrumb/BreadCrumb';
import PageHeader from '../PageHeader/PageHeader';
import AdsComponent from '../adsComponent';
import { RouteCabsV2SortBar } from './RouteCabsV2SortBar';
import {
  CLEAR_ALL_TEXT,
  isomorphicDateBarDateFormat,
  ORDER_BY_PRICE_ASC,
  ORDER_BY_RATING_DESC,
  selectedFilterBaseImage,
  SELECT_FILTER_TEXT
} from '../../constants/miscellaneousConstants';
import {
  Filter,
  sortOptionTypes,
  TopRoutesInfoFromSourceToDestination
} from '../../pages/RouteTemplate/RouteDataTypeChecker';
import { CabData, Template } from '../../pages/runtime-typechecker';
import InboundCallBox from '../InboundCall/InboundCallBox';
import MultiCityPackages from '../MultiCityPackages/MultiCityPackages';
import PackageBanner from '../PackageBanner/PackageBanner';

const dayjs = require('dayjs');

export const RouteCabsV2FilteredCabs = ({
  packages,
  data,
  filters,
  sourceCityName,
  destinationCityName,
  isMobileDevice,
  sourceCityCode,
  destinationCityCode,
  forwardDateCount = 0,
  templateData,
  inboundCallText,
  inboundCallNumber,
  showInboundCallNumberInDesktop,
  packageData
}: {
  data: TopRoutesInfoFromSourceToDestination | null | undefined;
  filters: Filter[] | null | undefined;
  sourceCityName: string;
  destinationCityName: string;
  isMobileDevice: boolean;
  sourceCityCode: string;
  destinationCityCode: string;
  forwardDateCount: number;
  templateData: Template;
  inboundCallText: string | undefined;
  inboundCallNumber: string | undefined;
  showInboundCallNumberInDesktop: boolean;
  packages?: any[];
  packageData: any;
}) => {
  const packageAvailabilityOrderMapDefault = new Map();
  if (data?.cabsList && data.cabsList.length > 0) {
    data.cabsList.forEach(cabInfo => {
      if (cabInfo.packages && cabInfo.packages.length > 0) {
        packageAvailabilityOrderMapDefault.set(cabInfo['order'], false);
      }
    });
  }
  const filterTypes = (filters || []).map(({ fieldName }) => fieldName);
  const defaultFilterStates = {};
  filterTypes.forEach(type => {
    defaultFilterStates[type] = new Set();
  });

  const [
    packageAvailabilityOrderMap,
    setPackageAvailabilityOrderMap
  ] = useState<Map<number, boolean>>(packageAvailabilityOrderMapDefault);

  const [sortType, setSortType] = useState<sortOptionTypes>(ORDER_BY_PRICE_ASC);
  const [sortOptionsOpen, setSortOptionsOpen] = useState<boolean>(false);
  const [selectedFilters, setSelectedFilters] = useState<Set<string>>(
    new Set()
  );
  const [filterStates, setFilterStates] = useState<any>(defaultFilterStates);

  const dateListForSlide: string[] = [];
  const currentDate = new Date();
  for (let i = 0; i < 90 + 1; i++) {
    dateListForSlide.push(
      dayjs(currentDate)
        .add(i, 'day')
        .format(isomorphicDateBarDateFormat)
    );
  }
  const filterFunctions = {};

  filterTypes.forEach(type => {
    filterFunctions[type] = {
      fieldName: type,
      addfieldValue: value => {
        filterStates[type].add(value);
        const newFilterStates = new Set(filterStates[type]);
        setFilterStates({ ...filterStates, [type]: newFilterStates });
      },
      deleteFieldValue: value => {
        filterStates[type].delete(value);
        const newFilterStates = new Set(filterStates[type]);
        setFilterStates({ ...filterStates, [type]: newFilterStates });
      },
      filterUsed: () => {
        return filterStates[type].size > 0;
      },
      filterHasValue: value => {
        return filterStates[type].has(value);
      }
    };
  });

  const isShown = cabInfo => {
    // when none of the filter type is selected
    if (
      andOfElements(
        Object.keys(filterStates).map(
          type => !filterFunctions[type].filterUsed()
        )
      ) == true
    )
      return true;

    //and of(for a filter type when either filter is selected and matches filter value or filter not selected)
    return andOfElements(
      Object.keys(filterStates).map(type => {
        return (
          (filterFunctions[type].filterUsed() &&
            filterFunctions[type].filterHasValue(cabInfo[type])) ||
          !filterFunctions[type].filterUsed()
        );
      })
    );
  };

  const getFilteredCabTypeCount = (value, field) => {
    let len = 0;

    const selectedCategory = getSelectedCatFromSelectedFilters();
    // when selected category is not equal to field, get count from filtered cabs
    if (data) {
      if (selectedCategory != field) {
        len = data.cabsList
          .filter(cabInfo => {
            return isShown(cabInfo);
          })
          .filter(cabInfo => {
            return cabInfo[field] == value;
          }).length;
      }
      // when selected category is equal to field, get count from non filtered cabs, when filter type is same avoid filtering
      if (selectedCategory == field) {
        len = data.cabsList
          .filter(cabInfo => {
            if (
              orOfElements(
                filterTypes
                  .filter(type => type != field)
                  .map(
                    type =>
                      filterFunctions[type].filterUsed() &&
                      !filterFunctions[type].filterHasValue(cabInfo[type])
                  )
              )
            ) {
              return false;
            }
            return true;
          })
          .filter(cabInfo => {
            return cabInfo[field] == value;
          }).length;
      }
    }
    return len;
  };
  const sortCabsListWithSelectedOrder = (
    cabsList: null | undefined | CabData[]
  ): CabData[] | null | undefined => {
    if (!cabsList) return cabsList;
    if (sortType == ORDER_BY_PRICE_ASC) {
      cabsList = cabsList.sort((cabInfo1, cabInfo2) =>
        Number(cabInfo1.total_amount) < Number(cabInfo2.total_amount) ? -1 : 1
      );
    }
    if (sortType == ORDER_BY_RATING_DESC) {
      cabsList = cabsList.sort((cabInfo1, cabInfo2) =>
        Number(cabInfo1.overall_rating) < Number(cabInfo2.overall_rating)
          ? 1
          : -1
      );
    }
    return cabsList;
  };

  const getSelectedCatFromSelectedFilters = () => {
    if (selectedFilters.size == 1) {
      return selectedFilters.values().next().value;
    } else if (selectedFilters.size > 1) {
      return Array.from(selectedFilters).pop() as string;
    }
    return null;
  };

  const appliedFilters = () => {
    {
      const ans =
        filters && filters.length > 0
          ? filters.map(({ fieldName, filterValues }) => {
              return filterValues.map(filterValue => {
                return filterStates[fieldName].has(filterValue) ? (
                  <div
                    className="appliedFilterButton"
                    onClick={() => {
                      filterFunctions[fieldName].deleteFieldValue(filterValue);
                      removeFilterOption(fieldName);
                    }}
                  >
                    <p className="inlineFlex latoBold font14">
                      {filterValue}
                      <span>
                        <img
                          className="appendLeft8"
                          width={12}
                          height={12}
                          src={selectedFilterBaseImage}
                        />
                      </span>
                    </p>
                  </div>
                ) : null;
              });
            })
          : null;
      return ans;
    }
  };

  const clearFilter = () => {
    setFilterStates(defaultFilterStates);
    setSelectedFilters(new Set());
  };

  const removeFilterOption = filterToRemove => {
    const removeFilter = orOfElements(
      filterTypes.map(
        type => filterToRemove == type && !filterFunctions[type].filterUsed()
      )
    );
    if (removeFilter) {
      selectedFilters.delete(filterToRemove);
      const newSelectedFilter = new Set(selectedFilters);
      setSelectedFilters(newSelectedFilter);
    }
  };

  const addFilterOption = filterToAdd => {
    selectedFilters.add(filterToAdd);
    const newSelectedFilter = new Set(selectedFilters);
    setSelectedFilters(newSelectedFilter);
  };

  const applyFadeEffectOnFilters = (value, fieldName) => {
    return orOfElements(
      filterTypes.map(type => {
        return (
          filterFunctions[type].filterUsed() &&
          andOfElements(
            filterTypes.map(
              type => !filterFunctions[type].filterHasValue(value)
            )
          ) &&
          getFilteredCabTypeCount(value, fieldName) == 0
        );
      })
    );
  };
  const setPackageAvailabilityOrderMapWrapper = (idx, val) => {
    const newMap = new Map(packageAvailabilityOrderMap);
    newMap.set(idx, val);
    setPackageAvailabilityOrderMap(newMap);
  };
  return (
    <>
      <div style={{ display: 'none' }} className="desktopShow">
        <RouteCabsV2SortBar
          sourceCityName={sourceCityName}
          destinationCityName={destinationCityName}
          data={data}
          sortOptionsOpen={sortOptionsOpen}
          sortType={sortType}
          setSortOptionsOpen={setSortOptionsOpen}
          setSortType={setSortType}
        />
      </div>
      <div className="cabCardBackgroundV2">
        <div
          className="container whiteBackground"
          style={{ paddingLeft: 16, paddingRight: 16 }}
        >
          <div>
            <div className="makeFlex">
              <BreadCrumb breadcrumb={templateData.breadcrumb} />
              {showInboundCallNumberInDesktop ? (
                <InboundCallBox
                  inboundCallText={inboundCallText}
                  inboundCallNumber={inboundCallNumber}
                />
              ) : null}
            </div>
            <PageHeader
              title={templateData.h1Title}
              extraInfo={templateData.metaDescription}
              isMobileDevice={isMobileDevice}
              url={templateData.sampleUrl}
            />
            {/* {
              <div>
                <AdsComponent
                  template={'Route'}
                  placedAt={'top'}
                  isMobileDevice={isMobileDevice}
                  desktopAdConfig={{
                    position: templateData.bannerDesktop
                      ? templateData.bannerDesktop
                      : 'none',
                    class: 'desktopShow appendBottom20 adsLayout',
                    style: {}
                  }}
                  mobileAdConfig={{
                    position: templateData.bannerMobile
                      ? templateData.bannerMobile
                      : 'none',
                    class: 'mobileShow appendBottom20 adsLayout',
                    style: { marginTop: null }
                  }}
                />
              </div>
            } */}
            {/* {packages && packages.length > 0 ? (
              <div>
                <h2 className="blackText">{`Popular packages from ${sourceCityName} to ${destinationCityName}`}</h2>
                <br />
                <MultiCityPackages
                  removeLeftRightPadding={true}
                  packages={packages}
                  disableStopFilter={true}
                  disableCabCardImage={true}
                  stopType={'city'}
                />
              </div>
            ) : null} */}
          </div>
        </div>
        <div className="container">
          {packageData && (
            <PackageBanner
              packageData={packageData}
              pageUrl={templateData.sampleUrl}
            />
          )}
        </div>
        <div className="container makeFlex">
          <div style={{ display: 'none' }} className="desktopShow filterPanel">
            <div className="stickyTop95 appendBottom50">
              {orOfElements(
                filterTypes.map(type => filterFunctions[type].filterUsed())
              ) ? (
                <div>
                  <div className="makeFlex spaceBetween end appendBottom10">
                    <p className="latoBold font20">Applied Filters</p>
                    <a className="font12 latoBold" onClick={clearFilter}>
                      {CLEAR_ALL_TEXT}
                    </a>
                  </div>
                  <div className="makeFlex wrap">{appliedFilters()}</div>
                </div>
              ) : null}
              <p className="latoBold font20 appendTop15 appendBottom20">
                {SELECT_FILTER_TEXT}
              </p>
              {filters && filters.length > 0
                ? filters.map(({ filterName, filterValues, fieldName }) => (
                    <div className="appendBottom30 ">
                      <p className="latoBold font16 appendBottom15">
                        {filterName}
                      </p>
                      {filterValues.map(value => {
                        const isFaded = applyFadeEffectOnFilters(
                          value,
                          fieldName
                        );
                        return (
                          <div
                            className={`makeFlex appendBottom15  ${
                              isFaded ? 'fadeEff' : ''
                            }`}
                          >
                            <></>
                            <input
                              type="checkbox"
                              className="cursorPointer checkBoxDimensionRouteCabsV2"
                              checked={Boolean(
                                (() => {
                                  return filterStates[fieldName].has(value);
                                })()
                              )}
                              onChange={e => {
                                if (e.target.checked) {
                                  filterFunctions[fieldName].addfieldValue(
                                    value
                                  );
                                  addFilterOption(fieldName);
                                } else {
                                  filterFunctions[fieldName].deleteFieldValue(
                                    value
                                  );
                                  removeFilterOption(fieldName);
                                }
                              }}
                            />
                            <label
                              className={`paddingL10 vertlCenter ${
                                filterFunctions[fieldName].filterHasValue(value)
                                  ? 'latoBold'
                                  : ''
                              }`}
                            >
                              {value}
                            </label>
                            <p className="font12 lightGreyText pushRight pushBottom vertlCenter">
                              ({getFilteredCabTypeCount(value, fieldName)})
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  ))
                : null}
            </div>
          </div>
          <div className="fullWidth">
            <div
              style={{ display: 'none' }}
              className="desktopShow  makeFlex fullWidth"
            >
              <Slides
                sourceCityCode={sourceCityCode}
                destinationCityCode={destinationCityCode}
                slideElements={dateListForSlide}
                selectedElement={dateListForSlide[forwardDateCount]}
              />
            </div>
            <RouteCabsV2
              tripBenefits={data?.tripBenefits}
              packageAvailabilityOrderMap={packageAvailabilityOrderMap}
              setPackageAvailabilityOrderMap={
                setPackageAvailabilityOrderMapWrapper
              }
              data={sortCabsListWithSelectedOrder(data?.cabsList)}
              topRoutesInfoFromSourceToDestinationIsomorphic={data}
              isShown={isShown}
              sourceCityName={sourceCityName}
              destinationCityName={destinationCityName}
              seoUrl={''}
              isMobileDevice={isMobileDevice}
            />
          </div>
        </div>
      </div>
    </>
  );
};
